import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import aiLogger from '../utils/aiLogger';
import AutomationManualIntake from './AutomationManualIntake';
import AutomationCaseDetailDrawer from './AutomationCaseDetailDrawer';
import AutomationKnowledge from './AutomationKnowledge';
import {
  ACTION_LABELS,
  DEFAULT_TABLE_FILTER,
  filterUiActions,
  formatAutomationActionError,
  formatCaseRowSubtitle,
  formatDateTime,
  formatPrLinkLabel,
  formatPrStatusLabel,
  formatSourceLabel,
  formatTableAnalysisLabel,
  getDuplicateExistingCase,
  hasPrActivity,
  isDuplicateCreateError,
  isMockPrEvidence,
  isTransientDisplayStatus,
  matchesTableFilter,
  MULTIPLE_SECTIONS_WARNING,
  pickPrimaryActions,
  POLL_MS,
  prStatusChipColor,
  shouldShowPrLink,
  statusChipColor,
  TABLE_FILTERS,
} from './automationCaseUtils';

function mergeCaseIntoList(cases, updated) {
  if (!updated?.case_id) return cases;
  const idx = cases.findIndex((c) => c.case_id === updated.case_id);
  if (idx < 0) return [updated, ...cases];
  const next = [...cases];
  next[idx] = { ...next[idx], ...updated };
  return next;
}

function mapDrawerSection(action) {
  if (action === 'view_generated_files') return 'generation';
  if (action === 'view_execution_history' || action === 'view_failure') return 'execution';
  return 'overview';
}

function extractUpdatedCase(response) {
  if (!response || typeof response !== 'object') return null;
  return response.case || response;
}

function actionSuccessMessage(action, updatedCase) {
  const status = updatedCase?.display_status || '';
  if (action === 'approve') return 'Case approved for automation.';
  if (action === 'generate') {
    return status.includes('Queued') || status === 'Generating'
      ? 'Generation started.'
      : 'Generation request submitted.';
  }
  if (action === 'run' || action === 'run_again') {
    return status.includes('Queued') || status.includes('Running')
      ? 'Execution started.'
      : 'Execution request submitted.';
  }
  if (action === 'mark_obsolete') return 'Case marked obsolete.';
  if (action === 'analyze') return 'Analysis started.';
  return null;
}

const AutomationCases = ({ selectedProject, apiClient, onNavigateToSettings }) => {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [createSuccess, setCreateSuccess] = useState(null);
  const [createWarnings, setCreateWarnings] = useState([]);
  const [duplicateConflict, setDuplicateConflict] = useState(null);
  const [pendingCreatePayload, setPendingCreatePayload] = useState(null);
  const [tableFilter, setTableFilter] = useState(DEFAULT_TABLE_FILTER);
  const [actionError, setActionError] = useState(null);
  const [actionSuccess, setActionSuccess] = useState(null);
  const [busyCaseId, setBusyCaseId] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuCase, setMenuCase] = useState(null);
  const [drawerCaseId, setDrawerCaseId] = useState(null);
  const [drawerSection, setDrawerSection] = useState('overview');
  const [drawerRefreshToken, setDrawerRefreshToken] = useState(0);
  const [automationView, setAutomationView] = useState('cases');

  const projectId = selectedProject?.project_id;

  const fetchCases = useCallback(async () => {
    if (!projectId || !apiClient?.listAutomationCases) return;
    setLoading(true);
    setError(null);
    try {
      const includeObsolete = tableFilter === 'obsolete' || tableFilter === 'all';
      const response = await apiClient.listAutomationCases(projectId, { includeObsolete });
      setCases(response.cases || []);
    } catch (err) {
      aiLogger.error('Failed to list automation cases', err);
      setError(err?.message || 'Failed to load automation cases.');
    } finally {
      setLoading(false);
    }
  }, [projectId, apiClient, tableFilter]);

  useEffect(() => {
    fetchCases();
  }, [fetchCases]);

  useEffect(() => {
    setCases([]);
    setDrawerCaseId(null);
    setAddModalOpen(false);
    setTableFilter(DEFAULT_TABLE_FILTER);
    setAutomationView('cases');
  }, [projectId]);

  const filteredCases = useMemo(
    () => cases.filter((caseRow) => matchesTableFilter(caseRow, tableFilter)),
    [cases, tableFilter]
  );

  const transientCaseIds = useMemo(
    () => cases.filter((c) => isTransientDisplayStatus(c.display_status)).map((c) => c.case_id),
    [cases]
  );
  const transientPollKey = transientCaseIds.join('|');

  useEffect(() => {
    if (!projectId || !apiClient?.getAutomationCase || transientCaseIds.length === 0) return undefined;

    let cancelled = false;
    const timer = setInterval(async () => {
      if (cancelled) return;
      try {
        const updates = await Promise.all(
          transientCaseIds.map((caseId) => apiClient.getAutomationCase(caseId))
        );
        if (cancelled) return;
        setCases((prev) => updates.reduce((acc, item) => mergeCaseIntoList(acc, item), prev));
      } catch (err) {
        aiLogger.error('Automation cases poll failed', err?.message || err);
      }
    }, POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [projectId, apiClient, transientPollKey]); // eslint-disable-line react-hooks/exhaustive-deps -- poll key tracks transient id set

  const openDrawer = (caseRow, section = 'overview') => {
    setDrawerCaseId(caseRow.case_id);
    setDrawerSection(section);
  };

  const openAddCaseModal = () => {
    setCreateError(null);
    setCreateWarnings([]);
    setCreateSuccess(null);
    setDuplicateConflict(null);
    setPendingCreatePayload(null);
    setAddModalOpen(true);
  };

  const handleKnowledgeCtaClick = () => {
    const repoCard = document.querySelector('[data-testid="automation-knowledge-repo-card"]');
    repoCard?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const drawerListCase = useMemo(
    () => cases.find((c) => c.case_id === drawerCaseId) || null,
    [cases, drawerCaseId]
  );

  const applyCaseUpdate = (response, action) => {
    const updated = extractUpdatedCase(response);
    if (!updated?.case_id) return null;
    setCases((prev) => mergeCaseIntoList(prev, updated));
    if (drawerCaseId === updated.case_id) {
      setDrawerRefreshToken((token) => token + 1);
    }
    const message = actionSuccessMessage(action, updated);
    if (message) {
      setActionSuccess(message);
      setActionError(null);
    }
    return updated;
  };

  const finalizeCreate = (created, warnings = []) => {
    setCases((prev) => mergeCaseIntoList(prev, created));
    setAddModalOpen(false);
    setDuplicateConflict(null);
    setPendingCreatePayload(null);
    setCreateSuccess(`Automation case "${created.title}" created. Review readiness in the detail drawer.`);
    openDrawer(created, 'overview');
    if (warnings.length) {
      setActionError(null);
      setCreateWarnings(warnings);
    }
  };

  const handleCreateCase = async ({ content, source, filename, uploadTitle }, { forceDuplicate = false } = {}) => {
    if (!projectId || !content?.trim()) return;
    setCreating(true);
    setCreateError(null);
    setCreateWarnings([]);
    if (!forceDuplicate) {
      setDuplicateConflict(null);
      setPendingCreatePayload(null);
    }
    const payload = { content, source, filename, uploadTitle };
    try {
      const created = await apiClient.createAutomationCase(projectId, {
        sourceText: content,
        sourceType: source === 'txt_upload' ? 'txt_upload' : 'paste',
        sourceFilename: filename,
        title: uploadTitle?.trim() || null,
        analyze: true,
        forceDuplicate,
      });
      const warnings = created.warnings || [];
      if (created.multiple_cases_detected && !warnings.includes(MULTIPLE_SECTIONS_WARNING)) {
        warnings.push(MULTIPLE_SECTIONS_WARNING);
      }
      finalizeCreate(created, warnings);
    } catch (err) {
      if (isDuplicateCreateError(err)) {
        setDuplicateConflict(getDuplicateExistingCase(err));
        setPendingCreatePayload(payload);
        setCreateError(null);
      } else {
        setCreateError(err?.message || 'Failed to create automation case.');
      }
    } finally {
      setCreating(false);
    }
  };

  const handleOpenExistingDuplicate = async () => {
    const existing = duplicateConflict;
    if (!existing?.case_id || !apiClient?.getAutomationCase) return;
    try {
      const detail = await apiClient.getAutomationCase(existing.case_id);
      setCases((prev) => mergeCaseIntoList(prev, detail));
      setAddModalOpen(false);
      setDuplicateConflict(null);
      setPendingCreatePayload(null);
      openDrawer(detail, 'overview');
    } catch (err) {
      setCreateError(err?.message || 'Failed to open existing automation case.');
    }
  };

  const handleForceDuplicateCreate = async () => {
    if (!pendingCreatePayload) return;
    await handleCreateCase(pendingCreatePayload, { forceDuplicate: true });
  };

  const runCaseAction = async (caseRow, action) => {
    if (!caseRow?.case_id || busyCaseId) return null;
    setActionError(null);
    setBusyCaseId(caseRow.case_id);

    try {
      if (action === 'view_details') {
        openDrawer(caseRow, 'overview');
        return null;
      }
      if (action === 'view_generated_files') {
        openDrawer(caseRow, 'generation');
        return null;
      }
      if (action === 'view_execution_history' || action === 'view_failure') {
        openDrawer(caseRow, 'execution');
        return null;
      }

      let response;
      if (action === 'analyze') {
        response = await apiClient.analyzeAutomationCase(caseRow.case_id);
        return applyCaseUpdate(response, action);
      }
      if (action === 'approve') {
        response = await apiClient.approveAutomationCase(caseRow.case_id);
        return applyCaseUpdate(response, action);
      }
      if (action === 'generate') {
        response = await apiClient.generateAutomationCase(caseRow.case_id);
        return applyCaseUpdate(response, action);
      }
      if (action === 'run' || action === 'run_again') {
        response = await apiClient.runAutomationCase(caseRow.case_id);
        return applyCaseUpdate(response, action);
      }
      if (action === 'mark_obsolete') {
        response = await apiClient.markAutomationCaseObsolete(caseRow.case_id);
        const updated = applyCaseUpdate(response, action);
        if (drawerCaseId === caseRow.case_id) {
          setDrawerCaseId(null);
        }
        return updated;
      }
      return null;
    } catch (err) {
      setActionError(formatAutomationActionError(err, `Failed to run action: ${action}`));
      setActionSuccess(null);
      return null;
    } finally {
      setBusyCaseId(null);
      setMenuAnchor(null);
      setMenuCase(null);
    }
  };

  const handleMenuOpen = (event, caseRow) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuCase(caseRow);
  };

  const renderRowActions = (caseRow) => {
    const allowed = filterUiActions(caseRow.allowed_actions || []);
    const primary = pickPrimaryActions(allowed);
    const menuActions = allowed.filter((a) => a !== primary);

    return (
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
        {primary ? (
          <Button
            size="small"
            variant="contained"
            disabled={busyCaseId === caseRow.case_id}
            onClick={(e) => {
              e.stopPropagation();
              runCaseAction(caseRow, primary);
            }}
            data-testid={`row-primary-action-${caseRow.case_id}`}
          >
            {ACTION_LABELS[primary] || primary}
          </Button>
        ) : null}
        {menuActions.length ? (
          <IconButton
            size="small"
            aria-label="More actions"
            onClick={(e) => handleMenuOpen(e, caseRow)}
            data-testid={`row-actions-menu-${caseRow.case_id}`}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        ) : null}
      </Stack>
    );
  };

  const renderPrStatusCell = (caseRow) => {
    if (!hasPrActivity(caseRow)) {
      return (
        <Typography variant="body2" color="text.secondary" data-testid={`case-pr-status-${caseRow.case_id}`}>
          —
        </Typography>
      );
    }
    const isMock = isMockPrEvidence(caseRow);
    const label = formatPrStatusLabel(caseRow.latest_pr_status, { isMock });
    const prUrl = caseRow.latest_pr_url;
    const showLink = shouldShowPrLink(caseRow);
    return (
      <Stack spacing={0.5} alignItems="flex-start" data-testid={`case-pr-status-${caseRow.case_id}`}>
        <Chip
          label={label}
          size="small"
          color={prStatusChipColor(caseRow.latest_pr_status, { isMock })}
          variant={isMock ? 'outlined' : 'filled'}
          data-testid={`case-pr-badge-${caseRow.case_id}`}
        />
        {showLink && prUrl ? (
          <Link
            href={prUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            onClick={(e) => e.stopPropagation()}
            data-testid={`case-pr-link-${caseRow.case_id}`}
          >
            {formatPrLinkLabel(caseRow)}
          </Link>
        ) : null}
        {isMock && prUrl ? (
          <Typography variant="caption" color="text.secondary" data-testid={`case-pr-mock-note-${caseRow.case_id}`}>
            Dev/mock evidence — not a real GitHub PR
          </Typography>
        ) : null}
      </Stack>
    );
  };

  return (
    <Box data-testid="automation-cases-page">
      <Tabs
        value={automationView}
        onChange={(_, value) => setAutomationView(value)}
        sx={{ mb: 2 }}
        data-testid="automation-view-tabs"
      >
        <Tab value="cases" label="Automation cases" data-testid="automation-view-tab-cases" />
        <Tab value="knowledge" label="Automation Knowledge" data-testid="automation-view-tab-knowledge" />
      </Tabs>

      {automationView === 'knowledge' ? (
        <>
          <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }} data-testid="automation-knowledge-cta">
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
              Add Automation Knowledge
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Bring in repository-discovered automation metadata to improve future coverage, impact analysis, and automation intelligence.
            </Typography>
            <Button
              variant="outlined"
              onClick={handleKnowledgeCtaClick}
              data-testid="automation-knowledge-cta-button"
            >
              Add Automation Knowledge
            </Button>
          </Paper>
          <AutomationKnowledge
            selectedProject={selectedProject}
            apiClient={apiClient}
            onNavigateToSettings={onNavigateToSettings}
          />
        </>
      ) : (
        <>
      <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }} data-testid="automation-cases-cta">
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          Add Automation Cases
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Create or review automation-ready test cases from approved manual test coverage.
        </Typography>
        <Button
          variant="outlined"
          onClick={openAddCaseModal}
          data-testid="automation-cases-cta-button"
        >
          Add Automation Cases
        </Button>
      </Paper>

      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} sx={{ mb: 2 }} spacing={1}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Automation</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage automation test cases from manual input through generation and execution.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openAddCaseModal}
          data-testid="add-automation-case-button"
        >
          Add Automation Test Case
        </Button>
      </Stack>

      {createSuccess ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCreateSuccess(null)}>
          {createSuccess}
        </Alert>
      ) : null}

      {createWarnings.length ? (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setCreateWarnings([])}>
          {createWarnings.join(' ')}
        </Alert>
      ) : null}

      {actionSuccess ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setActionSuccess(null)}>
          {actionSuccess}
        </Alert>
      ) : null}

      {actionError ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      ) : null}

      {error ? (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={(
            <Button color="inherit" size="small" onClick={fetchCases}>
              Retry
            </Button>
          )}
        >
          {error}
        </Alert>
      ) : null}

      <Tabs
        value={tableFilter}
        onChange={(_, value) => setTableFilter(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2 }}
        data-testid="automation-cases-filters"
      >
        {Object.entries(TABLE_FILTERS).map(([key, label]) => (
          <Tab key={key} value={key} label={label} data-testid={`automation-filter-${key}`} />
        ))}
      </Tabs>

      <Paper variant="outlined">
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={32} data-testid="automation-cases-loading" />
          </Box>
        ) : filteredCases.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, px: 2 }} data-testid="automation-cases-empty">
            <Typography variant="body1" sx={{ mb: 1 }}>
              {cases.length === 0
                ? 'No automation test cases yet.'
                : `No ${TABLE_FILTERS[tableFilter] || 'matching'} automation cases.`}
            </Typography>
            {cases.length === 0 ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Add or upload a manual test case to analyze readiness and prepare automation.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setAddModalOpen(true)}
                  data-testid="automation-cases-empty-cta"
                >
                  Add Automation Test Case
                </Button>
              </>
            ) : null}
          </Box>
        ) : (
          <TableContainer>
            <Table size="small" data-testid="automation-cases-table">
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Analysis</TableCell>
                  <TableCell>Automation Status</TableCell>
                  <TableCell>PR Status</TableCell>
                  <TableCell>Last Run</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCases.map((caseRow) => (
                  <TableRow
                    key={caseRow.case_id}
                    hover
                    data-testid={`automation-case-row-${caseRow.case_id}`}
                  >
                    <TableCell>
                      <Tooltip title={formatCaseRowSubtitle(caseRow)} placement="top-start">
                        <Box>
                          <Button
                            variant="text"
                            size="small"
                            sx={{ textTransform: 'none', p: 0, minWidth: 0, fontWeight: 500, display: 'block' }}
                            onClick={() => openDrawer(caseRow, 'overview')}
                          >
                            {caseRow.title}
                          </Button>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 0.25 }}
                            data-testid={`case-row-subtitle-${caseRow.case_id}`}
                          >
                            {formatCaseRowSubtitle(caseRow)}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{formatSourceLabel(caseRow.source_type)}</TableCell>
                    <TableCell data-testid={`case-analysis-${caseRow.case_id}`}>
                      {formatTableAnalysisLabel(caseRow)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={caseRow.display_status}
                        size="small"
                        color={statusChipColor(caseRow.display_status)}
                        data-testid={`case-status-${caseRow.case_id}`}
                      />
                    </TableCell>
                    <TableCell>{renderPrStatusCell(caseRow)}</TableCell>
                    <TableCell>
                      {caseRow.last_execution_status
                        ? `${caseRow.last_execution_status} · ${formatDateTime(caseRow.last_execution_at)}`
                        : '—'}
                    </TableCell>
                    <TableCell>{formatDateTime(caseRow.updated_at)}</TableCell>
                    <TableCell align="right">{renderRowActions(caseRow)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => {
          setMenuAnchor(null);
          setMenuCase(null);
        }}
      >
        {(menuCase?.allowed_actions || [])
          .filter((action) => filterUiActions([action]).length > 0)
          .filter((action) => action !== pickPrimaryActions(filterUiActions(menuCase?.allowed_actions || [])))
          .map((action) => (
            <MenuItem
              key={action}
              disabled={busyCaseId === menuCase?.case_id}
              onClick={() => runCaseAction(menuCase, action)}
              data-testid={`menu-action-${action}`}
            >
              {ACTION_LABELS[action] || action}
            </MenuItem>
          ))}
      </Menu>

      <Dialog
        open={addModalOpen}
        onClose={() => {
          if (!creating) {
            setAddModalOpen(false);
            setDuplicateConflict(null);
            setPendingCreatePayload(null);
          }
        }}
        maxWidth="md"
        fullWidth
        data-testid="add-automation-case-modal"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Add Automation Test Case
          <IconButton aria-label="Close" onClick={() => setAddModalOpen(false)} disabled={creating}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Paste or upload one manual test package. The full input is stored as a single Automation Case.
          </Typography>
          {duplicateConflict ? (
            <Alert severity="warning" sx={{ mb: 2 }} data-testid="automation-case-duplicate-warning">
              An active Automation Case already exists for this uploaded input.
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2">
                  <strong>{duplicateConflict.title}</strong> — {duplicateConflict.display_status}
                </Typography>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleOpenExistingDuplicate}
                  data-testid="automation-case-open-existing"
                >
                  Open existing case
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  size="small"
                  onClick={handleForceDuplicateCreate}
                  disabled={creating}
                  data-testid="automation-case-force-duplicate"
                >
                  Create duplicate anyway
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setDuplicateConflict(null);
                    setPendingCreatePayload(null);
                  }}
                >
                  Cancel
                </Button>
              </Stack>
            </Alert>
          ) : null}
          <AutomationManualIntake
            busy={creating}
            error={createError}
            onSubmit={handleCreateCase}
            submitLabel="Create Automation Case"
            requireTitle={false}
            showTitleField
            accept=".txt,text/plain"
            supportedFormatsLabel="TXT"
            titleFormat="readiness"
            pasteTestId="automation-case-paste-input"
            fileInputTestId="automation-case-file-input"
            analyzeTestId="automation-case-create-submit"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddModalOpen(false)} disabled={creating}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <AutomationCaseDetailDrawer
        open={Boolean(drawerCaseId)}
        caseId={drawerCaseId}
        listCaseRow={drawerListCase}
        refreshToken={drawerRefreshToken}
        initialSection={drawerSection}
        apiClient={apiClient}
        busy={busyCaseId === drawerCaseId}
        onClose={() => setDrawerCaseId(null)}
        onAction={(caseRow, action) => {
          if (['view_details', 'view_generated_files', 'view_execution_history', 'view_failure'].includes(action)) {
            setDrawerSection(mapDrawerSection(action));
            return;
          }
          runCaseAction(caseRow, action);
        }}
      />
        </>
      )}
    </Box>
  );
};

export default AutomationCases;
